<template>
  <div class="sales-dashboard min-h-screen">
    <!-- Header Section -->
    <div class="sd-header">
      <div class="w-full px-4 sm:px-6 lg:px-8 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div class="sd-header__logo">
              <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
              </svg>
            </div>
            <div>
              <h1 class="text-xl font-semibold text-secondary-800">{{ $t('sales_dashboard.sales_tools') }}</h1>
              <p class="text-sm text-gray-500">{{ $t('sales_dashboard.sales_tools_subtitle') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 relative">
      <!-- Overlay Loading -->
      <div v-if="isLoading" class="absolute inset-0 bg-white/70 flex items-center justify-center z-10 min-h-[400px]">
        <div class="flex flex-col items-center gap-3">
          <div class="w-8 h-8 border-3 border-securdia-blue border-t-transparent rounded-full animate-spin"></div>
          <span class="text-sm text-secondary-500">{{ $t('sales_dashboard.loading_tools') }}</span>
        </div>
      </div>

      <div :class="{ 'opacity-50 pointer-events-none': isLoading }">
        <!-- Tools Stack - Full Width -->
        <div class="flex flex-col gap-6">
          <!-- Gross Margin Calculator (TB-kalkylator) -->
          <div class="w-full">
            <GrossMarginCalculator :products="products" :subcategories="subcategories" :user="user" />
          </div>
          
          <!-- Sales Velocity Calculator -->
          <div class="w-full">
            <SalesVelocityCalculator :contracts="contracts" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import GrossMarginCalculator from '@/components/sales/GrossMarginCalculator.vue';
import SalesVelocityCalculator from '@/components/sales/SalesVelocityCalculator.vue';

export default {
  name: 'SalesTools',
  title() {
    return `${this.$t('sales_dashboard.sales_tools')} | SecurCloud`;
  },
  components: {
    GrossMarginCalculator,
    SalesVelocityCalculator
  },
  data() {
    return {
      isLoading: true,
      products: [],
      user: null,
      subcategories: [],
      contracts: [],
      sellers: [],
      filters: {
        sellerId: '',
        year: '',
        quarter: '',
        month: '',
        category: '',
        contractType: 'all',
        startDate: '',
        endDate: ''
      }
    };
  },
  methods: {
    async fetchData() {
      this.isLoading = true;
      
      try {
        await Promise.all([
          this.fetchProducts(),
          this.fetchSubcategories(),
          this.fetchContracts(),
          this.fetchUser()
        ]);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        this.isLoading = false;
      }
    },
    async fetchUser(){
      // Get the currently logged-in user from Vuex store
      this.user = this.$store.state.user;
    },
    async fetchProducts() {
      try {
        const response = await this.axios.get(
          `${process.env.VUE_APP_SERVER_URL}/products`
        );
        this.products = response.data.data || [];
      } catch (error) {
        console.error('Error fetching products:', error);
        this.products = [];
      }
    },
    
    async fetchSubcategories() {
      try {
        const response = await this.axios.get(
          `${process.env.VUE_APP_SERVER_URL}/subcategories`
        );
        this.subcategories = response.data.data || [];
      } catch (error) {
        console.error('Error fetching subcategories:', error);
        this.subcategories = [];
      }
    },
    
    async fetchContracts() {
      try {
        const filterParams = this.getFilterParams();
        const params = new URLSearchParams({ limit: 500, ...filterParams });
        const response = await this.axios.get(
          `${process.env.VUE_APP_SERVER_URL}/sales/dashboard/contracts?${params}`
        );
        this.contracts = response.data.data || [];
      } catch (error) {
        console.error('Error fetching contracts:', error);
        this.contracts = [];
      }
    },
    
    async fetchSellers() {
      try {
        const response = await this.axios.get(
          `${process.env.VUE_APP_SERVER_URL}/sales/dashboard/sellers`
        );
        this.sellers = response.data.data || [];
      } catch (error) {
        console.error('Error fetching sellers:', error);
      }
    },
    
    getFilterParams() {
      const params = {};
      
      if (this.filters.sellerId) {
        params.seller_id = this.filters.sellerId;
      }
      
      if (this.filters.startDate) {
        params.start_date = this.filters.startDate;
      } else if (this.filters.year) {
        const year = parseInt(this.filters.year);
        let startMonth = 0;
        let endMonth = 11;
        
        if (this.filters.quarter) {
          const quarter = parseInt(this.filters.quarter);
          startMonth = (quarter - 1) * 3;
          endMonth = startMonth + 2;
        }
        
        if (this.filters.month) {
          startMonth = parseInt(this.filters.month) - 1;
          endMonth = startMonth;
        }
        
        params.start_date = new Date(year, startMonth, 1).toISOString().slice(0, 10);
        params.end_date = new Date(year, endMonth + 1, 0).toISOString().slice(0, 10);
      }
      
      if (this.filters.endDate) {
        params.end_date = this.filters.endDate;
      }
      
      if (this.filters.category) {
        params.category = this.filters.category;
      }
      
      if (this.filters.contractType && this.filters.contractType !== 'all') {
        params.contract_type = this.filters.contractType;
      }
      
      return params;
    }
  },
  created() {
    this.setPageTitle(this.$t('sales_dashboard.sales_tools'), 'sales_tools');
    this.fetchSellers();
    this.fetchData();
  }
};
</script>
